import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import DesignEventForm from '../../Components/Worldmap/DesignEventForm';
import ITCProcessor from './ITCProcessor';
import { worldmapStyles } from './worldmapStyles';
import EmbeddedMapViewer from '../../Components/Worldmap/EmbeddedMapViewer';

// Design Event Logos
import cityLogo from '../Worldmap/SVG/city_horizontal.png';
import ouiDesignLogo from '../Worldmap/SVG/oui_design_horizontal.webp';
import parisDesignWeekLogo from '../Worldmap/SVG/paris_design_week_horizontal.png';

const DesignEventManager = () => {
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [error, setError] = useState('');
    const [events, setEvents] = useState([]);
    const [editingEvent, setEditingEvent] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalEvents, setTotalEvents] = useState(0);
    const [isFormCollapsed, setIsFormCollapsed] = useState(true);
    const [activeTab, setActiveTab] = useState('list');
    const [filterCategory, setFilterCategory] = useState('all');
    const [selectedItemForMap, setSelectedItemForMap] = useState(null);

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            
            const { count, error: countError } = await supabase
                .from('itc_2025')
                .select('*', { count: 'exact', head: true });
            
            if (!countError) {
                setTotalEvents(count || 0);
            }

            const { data, error } = await supabase
                .from('itc_2025')
                .select('*')
                .order('date_debut_expo', { ascending: true });

            if (error) throw error;
            setEvents(data || []);
        } catch (error) {
            setError('Erreur lors du chargement des événements design : ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (eventId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cet événement design ?')) {
            try {
                setLoading(true);
                const { error } = await supabase
                    .from('itc_2025')
                    .delete()
                    .eq('id', eventId);

                if (error) throw error;
                
                setFeedback('Événement design supprimé avec succès');
                fetchEvents();
            } catch (error) {
                setError('Erreur lors de la suppression de l\'\u00e9vénement design : ' + error.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEdit = (event) => {
        setEditingEvent(event);
        setIsEditing(true);
        setIsFormCollapsed(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const filteredEvents = events.filter(event => {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = (
            event.nom_etablissement?.toLowerCase().includes(searchLower) ||
            event.adresse?.toLowerCase().includes(searchLower) ||
            event.description_projet?.toLowerCase().includes(searchLower) ||
            event.event_type?.toLowerCase().includes(searchLower)
        );
        
        const matchesCategory = filterCategory === 'all' || event.design_events === filterCategory;
        
        return matchesSearch && matchesCategory;
    });

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getEventStatusColor = (startDate, endDate) => {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (now < start) return '#ffc107'; // Yellow for upcoming
        if (now >= start && now <= end) return '#28a745'; // Green for ongoing
        return '#6c757d'; // Gray for past
    };

    const getEventStatus = (startDate, endDate) => {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (now < start) return 'À venir';
        if (now >= start && now <= end) return 'En cours';
        return 'Passé';
    };

    const styles = {
        tabContainer: {
            marginBottom: '30px',
            display: 'flex',
            gap: '10px'
        },
        filterContainer: {
            marginBottom: '20px',
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
        },
        eventCard: {
            padding: '25px 30px',
            borderBottom: '1px solid #e9ecef',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
        }
    };

    return (
        <div>
            {/* Embedded Map Viewer */}
            {selectedItemForMap && (
                <EmbeddedMapViewer 
                    item={selectedItemForMap}
                    type="event"
                    onClose={() => setSelectedItemForMap(null)}
                />
            )}

            {/* Statistics Card */}
            <div style={{
                ...worldmapStyles.statsCard,
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)',
                boxShadow: '0 10px 30px rgba(255, 107, 107, 0.3)'
            }}>
                <div>
                    <div style={worldmapStyles.statsNumber}>{totalEvents}</div>
                    <div style={worldmapStyles.statsLabel}>Total des Événements Design</div>
                </div>
                <div style={{ fontSize: '60px', opacity: '0.3' }}>🎨</div>
            </div>

            {/* Tab Navigation */}
            <div style={styles.tabContainer}>
                <button 
                    style={{
                        ...worldmapStyles.tabButton,
                        ...(activeTab === 'list' ? worldmapStyles.activeTabButton : {})
                    }}
                    onClick={() => setActiveTab('list')}
                >
                    <span style={{ marginRight: '8px' }}>📋</span>
                    Gérer Événements
                </button>
                <button 
                    style={{
                        ...worldmapStyles.tabButton,
                        ...(activeTab === 'upload' ? worldmapStyles.activeTabButton : {})
                    }}
                    onClick={() => setActiveTab('upload')}
                >
                    <span style={{ marginRight: '8px' }}>📁</span>
                    Téléchargement CSV
                </button>
            </div>

            {/* Feedback Messages */}
            {loading && (
                <div style={{ ...worldmapStyles.alert, backgroundColor: '#cfe2ff', color: '#084298', border: '1px solid #b6d4fe' }}>
                    <span style={{ marginRight: '10px' }}>⏳</span> Traitement en cours...
                </div>
            )}
            {feedback && (
                <div style={{ ...worldmapStyles.alert, ...worldmapStyles.successAlert }}>
                    <span style={{ marginRight: '10px' }}>✅</span> {feedback}
                </div>
            )}
            {error && (
                <div style={{ ...worldmapStyles.alert, ...worldmapStyles.errorAlert }}>
                    <span style={{ marginRight: '10px' }}>⚠️</span> {error}
                </div>
            )}

            {/* List Tab Content */}
            {activeTab === 'list' && (
                <>
                    {/* Add/Edit Form */}
                    {!isEditing && (
                        <div style={worldmapStyles.formSection}>
                            <div 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    cursor: 'pointer',
                                    marginBottom: '20px',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    transition: 'background-color 0.3s ease'
                                }}
                                onClick={() => setIsFormCollapsed(!isFormCollapsed)}
                            >
                                <span style={{ 
                                    marginRight: '15px', 
                                    fontSize: '24px',
                                    transform: isFormCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                                    transition: 'transform 0.3s ease',
                                    display: 'inline-block'
                                }}>
                                    ▶
                                </span>
                                <h3 style={{ margin: 0, color: '#212529' }}>
                                    <span style={{ marginRight: '10px' }}>➕</span>
                                    Ajouter un Nouvel Événement Design
                                </h3>
                            </div>
                            {!isFormCollapsed && (
                                <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
                                    <DesignEventForm 
                                        onSuccess={() => {
                                            fetchEvents();
                                            setFeedback('Événement design ajouté avec succès ! 🎉');
                                            setIsFormCollapsed(true);
                                        }}
                                        onError={(error) => setError(error.message)}
                                        isEditing={false}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {isEditing && editingEvent && (
                        <div style={worldmapStyles.formSection}>
                            <div style={{ marginBottom: '20px' }}>
                                <button 
                                    onClick={() => {
                                        setIsEditing(false);
                                        setEditingEvent(null);
                                    }}
                                    style={{
                                        ...worldmapStyles.button,
                                        backgroundColor: '#6c757d',
                                        color: 'white'
                                    }}
                                    className="button-hover"
                                >
                                    ← Retour à la Liste
                                </button>
                            </div>
                            <h3 style={{ marginBottom: '20px', color: '#212529' }}>
                                <span style={{ marginRight: '10px' }}>✏️</span>
                                Modifier l'Événement Design
                            </h3>
                            <DesignEventForm 
                                initialData={editingEvent}
                                isEditing={true}
                                onSuccess={() => {
                                    fetchEvents();
                                    setIsEditing(false);
                                    setEditingEvent(null);
                                    setFeedback('Événement design mis à jour avec succès ! ✨');
                                }}
                                onError={(error) => setError(error.message)}
                            />
                        </div>
                    )}

                    {/* Events List */}
                    <div style={{ marginTop: '40px' }}>
                        <h3 style={{ marginBottom: '30px', fontSize: '24px', fontWeight: '600', color: '#212529' }}>
                            Événements Design Existants
                        </h3>
                        
                        {/* Filters */}
                        <div style={styles.filterContainer}>
                            <span style={{ fontWeight: '500', color: '#495057' }}>Filtrer par catégorie :</span>
                            <select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                style={{
                                    padding: '8px 12px',
                                    border: '2px solid #e9ecef',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: 'white'
                                }}
                            >
                                <option value="all">Toutes les Catégories</option>
                                <option value="Oui Design! 2025">Oui Design! 2025</option>
                                <option value="In the City">In the City</option>
                                <option value="Paris Design Week 2025">Paris Design Week 2025</option>
                            </select>
                        </div>

                        {/* Search Bar */}
                        <input
                            type="text"
                            placeholder="🔍 Rechercher par lieu, adresse, description ou type..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={worldmapStyles.searchBar}
                            className="search-focus"
                        />

                        {/* Events List */}
                        <div style={worldmapStyles.listContainer}>
                            {filteredEvents.length === 0 ? (
                                <div style={worldmapStyles.emptyState}>
                                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</div>
                                    <h4>Aucun événement design trouvé</h4>
                                    <p>Essayez d'ajuster vos critères de recherche ou ajoutez un nouvel événement</p>
                                </div>
                            ) : (
                                filteredEvents.map(event => (
                                    <div 
                                        key={event.id} 
                                        style={{
                                            ...styles.eventCard,
                                            display: 'grid',
                                            gridTemplateColumns: '120px 1fr 100px',
                                            gap: '25px',
                                            alignItems: 'flex-start',
                                            minHeight: '120px',
                                            padding: '25px'
                                        }}
                                        className="list-item-hover event-responsive-row"
                                    >
                                        {/* Design Event Logo */}
                                        <div style={{
                                            width: '120px',
                                            height: '80px',
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            backgroundColor: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                                            border: '2px solid #f0f0f0',
                                            justifySelf: 'center'
                                        }}
                                        className="event-logo-image">
                                            {event.design_events === 'In the City' ? (
                                                <img
                                                    src={cityLogo}
                                                    alt="In The City"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        padding: '8px'
                                                    }}
                                                />
                                            ) : event.design_events === 'Oui Design! 2025' ? (
                                                <img
                                                    src={ouiDesignLogo}
                                                    alt="Oui Design! 2025"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        padding: '8px'
                                                    }}
                                                />
                                            ) : event.design_events === 'Paris Design Week 2025' ? (
                                                <img
                                                    src={parisDesignWeekLogo}
                                                    alt="Paris Design Week 2025"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        padding: '8px'
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    fontSize: '32px',
                                                    color: '#999'
                                                }}>
                                                    🎨
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Event Content */}
                                        <div style={{ 
                                            minWidth: 0, 
                                            overflow: 'hidden' 
                                        }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'flex-start', 
                                                justifyContent: 'space-between',
                                                marginBottom: '12px'
                                            }}>
                                                <div>
                                                    <div style={{ fontSize: '22px', fontWeight: '700', color: '#212529', marginBottom: '8px' }}>
                                                        {event.nom_etablissement || 'Événement Sans Titre'}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                                                        {event.event_type && (
                                                            <span style={{
                                                                ...worldmapStyles.badge,
                                                                backgroundColor: '#e8f4f8',
                                                                color: '#0277bd',
                                                                padding: '6px 14px',
                                                                fontSize: '12px',
                                                                fontWeight: '600'
                                                            }}>
                                                                📅 {event.event_type}
                                                            </span>
                                                        )}
                                                        {event.design_events && (
                                                            <span style={{
                                                                ...worldmapStyles.badge,
                                                                backgroundColor: '#fff3e0',
                                                                color: '#ef6c00',
                                                                padding: '6px 14px',
                                                                fontSize: '12px',
                                                                fontWeight: '600'
                                                            }}>
                                                                {event.design_events}
                                                            </span>
                                                        )}
                                                        {event.date_debut_expo && event.date_fin_expo && (
                                                            <span style={{
                                                                ...worldmapStyles.badge,
                                                                backgroundColor: getEventStatusColor(event.date_debut_expo, event.date_fin_expo) + '20',
                                                                color: getEventStatusColor(event.date_debut_expo, event.date_fin_expo),
                                                                border: `1px solid ${getEventStatusColor(event.date_debut_expo, event.date_fin_expo)}40`,
                                                                padding: '6px 14px',
                                                                fontSize: '12px',
                                                                fontWeight: '600'
                                                            }}>
                                                                ● {getEventStatus(event.date_debut_expo, event.date_fin_expo)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <p style={{ margin: '8px 0', color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
                                                📍 {event.adresse || 'Aucune adresse fournie'}
                                            </p>
                                            
                                            {event.description_projet && (
                                                <p style={{ 
                                                    margin: '10px 0', 
                                                    color: '#495057', 
                                                    fontSize: '14px',
                                                    lineHeight: '1.5',
                                                    maxHeight: '60px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {event.description_projet.length > 150 
                                                        ? event.description_projet.substring(0, 150) + '...'
                                                        : event.description_projet
                                                    }
                                                </p>
                                            )}

                                            <div style={{ display: 'flex', gap: '20px', marginTop: '15px', fontSize: '14px', color: '#6c757d' }}>
                                                {event.date_debut_expo && (
                                                    <span>🗓️ Début : {formatDate(event.date_debut_expo)}</span>
                                                )}
                                                {event.date_fin_expo && (
                                                    <span>🏁 Fin : {formatDate(event.date_fin_expo)}</span>
                                                )}
                                                {event.vernissage_date && (
                                                    <span>🥂 Vernissage: {formatDate(event.vernissage_date)}</span>
                                                )}
                                            </div>

                                            {(event.site_web || event.instagram || event.rsvp) && (
                                                <div style={{ marginTop: '15px', display: 'flex', gap: '15px' }}>
                                                    {event.site_web && (
                                                        <a 
                                                            href={event.site_web} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: '#0039d6',
                                                                textDecoration: 'none',
                                                                fontSize: '14px',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            🌐 Site Web
                                                        </a>
                                                    )}
                                                    {event.instagram && (
                                                        <span style={{ fontSize: '14px', color: '#6c757d' }}>
                                                            📸 {event.instagram}
                                                        </span>
                                                    )}
                                                    {event.rsvp && (
                                                        <a 
                                                            href={event.rsvp} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: '#28a745',
                                                                textDecoration: 'none',
                                                                fontSize: '14px',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            📝 RSVP
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Action Buttons */}
                                        <div style={{ 
                                            display: 'flex', 
                                            gap: '8px', 
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexDirection: 'column'
                                        }}>
                                            <button
                                                onClick={() => {
                                                    setSelectedItemForMap(event);
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                                style={{ 
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    width: '100px',
                                                    height: '32px',
                                                    padding: '0 12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '20px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    gap: '4px',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    marginBottom: '8px'
                                                }}
                                                className="button-hover map-button"
                                                title="Voir sur la map"
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                                                }}
                                            >
                                                🌍 Map
                                            </button>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => handleEdit(event)}
                                                    style={{ 
                                                        ...worldmapStyles.button, 
                                                        ...worldmapStyles.editButton,
                                                        width: '44px',
                                                        height: '44px',
                                                        padding: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: '10px',
                                                        fontSize: '20px',
                                                        color: 'white',
                                                        fontWeight: 'normal'
                                                    }}
                                                    className="button-hover icon-button"
                                                    title="Modifier cet événement design"
                                                >
                                                    ✎
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(event.id)}
                                                    style={{ 
                                                        ...worldmapStyles.button, 
                                                        ...worldmapStyles.deleteButton,
                                                        width: '44px',
                                                        height: '44px',
                                                        padding: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: '10px',
                                                        fontSize: '20px',
                                                        color: 'white',
                                                        fontWeight: 'normal'
                                                    }}
                                                    className="button-hover icon-button"
                                                    title="Supprimer cet événement design"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* CSV Upload Tab Content */}
            {activeTab === 'upload' && (
                <div style={worldmapStyles.formSection}>
                    <h3 style={{ marginBottom: '20px', color: '#212529' }}>
                        <span style={{ marginRight: '10px' }}>📊</span>
                        Processeur CSV pour Événements Design
                    </h3>
                    <ITCProcessor />
                </div>
            )}
        </div>
    );
};

export default DesignEventManager;